import { ResponseError } from 'superagent';

interface ArtifactValidationErrorSummary {
  path?: string;
  message?: string;
}

interface ArtifactValidationReference {
  artifactId?: string | null;
  artifactUrl?: string | null;
  slideId?: string | null;
  slideIds?: string[] | null;
  orchestrationId?: string | null;
  resultType?: string | null;
  validationErrors?: ArtifactValidationErrorSummary[];
}

interface ArtifactValidationErrorDetail {
  errorCode?: string;
  message?: string;
  artifacts?: ArtifactValidationReference[];
  validationErrors?: ArtifactValidationErrorSummary[];
}

const MAX_ARTIFACT_IDS_TO_DISPLAY = 5;

const getArtifactValidationDetail = (error: ResponseError): ArtifactValidationErrorDetail | undefined => {
  const detail = error?.response?.body?.detail;
  if (!detail || typeof detail !== 'object') {
    return undefined;
  }

  return detail as ArtifactValidationErrorDetail;
};

const getArtifactIds = (artifacts: ArtifactValidationReference[] = []): string[] => {
  const seenArtifactIds = new Set<string>();
  const artifactIds: string[] = [];

  for (const artifact of artifacts) {
    if (!artifact?.artifactId || seenArtifactIds.has(artifact.artifactId)) {
      continue;
    }
    seenArtifactIds.add(artifact.artifactId);
    artifactIds.push(artifact.artifactId);
  }

  return artifactIds;
};

const getFirstValidationError = (detail: ArtifactValidationErrorDetail): ArtifactValidationErrorSummary | undefined => {
  for (const artifact of detail.artifacts || []) {
    if (artifact.validationErrors?.length) {
      return artifact.validationErrors[0];
    }
  }

  return detail.validationErrors?.[0];
};

const formatArtifactContext = (artifact?: ArtifactValidationReference): string | undefined => {
  if (!artifact) {
    return undefined;
  }

  const contextParts: string[] = [];
  const slideReference = artifact.slideId || artifact.slideIds?.[0];

  if (slideReference) {
    contextParts.push(`slide ${slideReference}`);
  }
  if (artifact.orchestrationId) {
    contextParts.push(`orchestration ${artifact.orchestrationId}`);
  }
  if (artifact.resultType) {
    contextParts.push(`result ${artifact.resultType}`);
  }

  if (!contextParts.length) {
    return artifact.artifactUrl ? `Artifact URL: ${artifact.artifactUrl}` : undefined;
  }

  return `Context: ${contextParts.join(', ')}`;
};

export const isArtifactValidationError = (error: ResponseError): boolean => {
  return getArtifactValidationDetail(error)?.errorCode === 'artifact_validation_error';
};

export const getArtifactResultsErrorMessage = (error: ResponseError): string => {
  const detail = getArtifactValidationDetail(error);
  if (!detail || detail.errorCode !== 'artifact_validation_error') {
    return error?.response?.body?.message || error?.message;
  }

  const errorLines = [detail.message || 'One or more stored artifacts have invalid metadata.'];
  const artifactIds = getArtifactIds(detail.artifacts);

  if (artifactIds.length) {
    const displayedArtifactIds = artifactIds.slice(0, MAX_ARTIFACT_IDS_TO_DISPLAY);
    const hiddenArtifactCount = artifactIds.length - displayedArtifactIds.length;
    errorLines.push(
      `Artifact IDs: ${displayedArtifactIds.join(', ')}${
        hiddenArtifactCount > 0 ? ` (+${hiddenArtifactCount} more)` : ''
      }`
    );
  }

  const artifactContext = formatArtifactContext(detail.artifacts?.[0]);
  if (artifactContext) {
    errorLines.push(artifactContext);
  }

  const validationError = getFirstValidationError(detail);
  if (validationError?.path && validationError?.message) {
    errorLines.push(`Validation: ${validationError.path}: ${validationError.message}`);
  }

  return errorLines.join('\n');
};
